import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import emailjs from '@emailjs/browser';

// ── Data ──────────────────────────────────────────────────────────────────────

const protectionLayers = [
  {
    number: '01',
    icon: '🔐',
    title: 'Secret Family Code',
    subtitle: 'Human Verification Layer',
    desc: 'Every family sets a private code word known only to members. If someone calls claiming to be a family member from an unknown number, ask the code word. No correct answer — hang up immediately.',
    color: '#3B82F6',
  },
  {
    number: '02',
    icon: '📲',
    title: 'Family Verification Call',
    subtitle: 'Real-Time Cross Verification',
    desc: 'When a suspicious call claims to be a family member, Suraksha silently notifies the real member\'s phone instantly. One tap YES or NO. If NO — your phone shows a full-screen SCAM ALERT immediately.',
    color: '#8B5CF6',
  },
  {
    number: '03',
    icon: '🤖',
    title: 'AI Voice Analysis',
    subtitle: 'Voice Clone Detection',
    desc: 'Suraksha analyses the voice in real time during the call. Detects unnatural speech patterns and AI-generated voice artifacts. Shows a live confidence score: "87% likely AI generated voice."',
    color: '#06B6D4',
  },
  {
    number: '04',
    icon: '🚨',
    title: 'Behavioural Red Flag Detection',
    subtitle: 'Pattern Recognition Engine',
    desc: 'Automatically detects urgency language, requests for secrecy, unknown numbers claiming to be known persons, financial transfer requests, and emergency + money mentioned in the same call.',
    color: '#F59E0B',
  },
  {
    number: '05',
    icon: '🔒',
    title: 'Emergency Verification Protocol',
    subtitle: 'UPI Lock & Family Alert',
    desc: 'If a call claims family emergency and asks for money — Suraksha locks all UPI apps for 10 minutes, displays a countdown timer, and simultaneously alerts all family members to verify.',
    color: '#E63946',
  },
];

const features = [
  {
    icon: '📞',
    title: 'Scam Call Intelligence',
    desc: 'Real-time detection of scam calls. Cross-references against a live database of known fraud numbers. Alerts you before you even pick up.',
  },
  {
    icon: '🌐',
    title: 'Safe Browsing Shield',
    desc: 'Analyses URLs and websites in real time. Blocks phishing sites, fake banking portals, and malicious links sent via WhatsApp or SMS.',
  },
  {
    icon: '👴',
    title: 'Guardian Mode',
    desc: 'Silent protection for elderly family members. Runs in the background with zero interaction needed. The tech-savvy child manages everything remotely.',
  },
  {
    icon: '🏛️',
    title: 'Digital Arrest Alerts',
    desc: 'Detects the signature patterns of "digital arrest" scams — fake CBI, ED, and police impersonation calls — and alerts you with educational context.',
  },
  {
    icon: '📊',
    title: 'Data Exposure Scanner',
    desc: 'Checks if your phone number, email, or Aadhaar-linked data has appeared in known data breaches. Instant alert with recommended action steps.',
  },
  {
    icon: '🆘',
    title: 'SOS Emergency Response',
    desc: 'One-tap SOS that simultaneously calls your emergency contact, shares your live location, and notifies all family members on the Suraksha network.',
  },
  {
    icon: '👪',
    title: 'Family Shield Dashboard',
    desc: 'Manage protection for up to 4 family members from a single dashboard. View real-time protection status, alerts, and threat history for the whole family.',
  },
  {
    icon: '🔐',
    title: 'UPI App Lock',
    desc: 'During a suspected scam emergency, Suraksha overlays a verification screen over PhonePe, GPay, Paytm, and BHIM — preventing impulsive transfers.',
  },
  {
    icon: '🇮🇳',
    title: 'DPDP Act 2023 Compliant',
    desc: 'All data stored on Indian servers. AES-256 encrypted. We never sell your data, store call recordings, or share with advertisers. Ever.',
  },
];

const plans = [
  {
    name: 'Free',
    price: '₹0',
    period: '',
    desc: 'Try Suraksha. Experience the protection.',
    features: [
      '2 scam call blocks/month',
      'Basic scam alerts',
      'Safe browsing (limited)',
      'No AI guidance',
      'No SOS feature',
    ],
    cta: 'Join Waitlist',
    highlight: false,
    badge: '',
  },
  {
    name: 'Individual',
    price: '₹899',
    period: '/year',
    perDay: '₹2.46/day',
    desc: 'Full protection for you. Every feature, 1 device.',
    features: [
      'Unlimited scam call blocking',
      'AI Voice Clone detection',
      'Digital Arrest Alerts',
      'Safe Browsing Shield',
      'Data Exposure Scanner',
      'SOS Emergency Response',
      'AI Fraud Guidance',
    ],
    cta: 'Join Waitlist',
    highlight: false,
    badge: '',
  },
  {
    name: 'Family Shield',
    price: '₹1,999',
    period: '/year',
    perDay: '₹5.47/day',
    desc: 'Full protection for the whole family. 4 members.',
    features: [
      'Everything in Individual',
      'Protect up to 4 members',
      'Guardian Mode for elderly',
      'Family Verification Protocol',
      'VoiceGuard AI detection',
      'Emergency UPI lock',
      'Family Admin Dashboard',
      'Shared family threat alerts',
    ],
    cta: 'Join Waitlist',
    highlight: true,
    badge: '⭐ Most Popular',
  },
];

const stats = [
  { value: '₹1,750 Cr', label: 'Lost to cyber fraud yearly in India', icon: '💸' },
  { value: '340%', label: 'Rise in digital arrest scams in 2024', icon: '📈' },
  { value: '1 in 3', label: 'Indians have received a scam call', icon: '📞' },
  { value: '₹0', label: 'Recovered by victims on average', icon: '🚨' },
];

const timeline = [
  { phase: 'Phase 1', label: 'Core App Development', status: 'complete', desc: 'Architecture, consent framework, security layer' },
  { phase: 'Phase 2', label: 'Scam Intelligence Engine', status: 'complete', desc: 'Real-time call analysis and scam database' },
  { phase: 'Phase 3', label: 'AI Voice Guard', status: 'active', desc: 'Voice clone detection and behavioural analysis' },
  { phase: 'Phase 4', label: 'Beta Testing', status: 'upcoming', desc: 'Closed beta with 500 early access users' },
  { phase: 'Phase 5', label: 'Public Launch', status: 'upcoming', desc: 'Android + iOS release on Play Store and App Store' },
];

// ── Waitlist Form ─────────────────────────────────────────────────────────────

function WaitlistForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    plan: 'Family Shield',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await emailjs.send(
        'service_g9tvszg',
        'template_124o1la',
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          company: `Suraksha Waitlist — ${formData.plan} Plan`,
          message: `New Suraksha waitlist signup.\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nPlan Interest: ${formData.plan}`,
          to_name: 'Ashish Uniyal',
        },
        'u6gb-CCFGNTwlW3Ot'
      );
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please email us at a.uniyal@redcubicalsystems.com');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(59,130,246,0.2)',
    borderRadius: '10px',
    padding: '14px 16px',
    fontSize: '14px',
    color: '#fff',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '6px',
  };

  if (submitted) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '48px 32px',
        background: 'rgba(59,130,246,0.06)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: '16px',
      }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🛡️</p>
        <h3 style={{
          fontFamily: 'Barlow Condensed, sans-serif',
          fontSize: '26px', fontWeight: '900',
          color: '#fff', textTransform: 'uppercase',
          marginBottom: '10px',
        }}>
          You're on the List!
        </h3>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: '1.6',
        }}>
          We'll notify you the moment Suraksha launches.
          You'll be among the first protected.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
    >
      <div>
        <label style={labelStyle}>Your Name *</label>
        <input
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Full name"
          required
          style={inputStyle}
          onFocus={e => (e.target.style.borderColor = '#3B82F6')}
          onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
        />
      </div>

      <div className="suraksha-form-row">
        <div>
          <label style={labelStyle}>Email *</label>
          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Phone *</label>
          <input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      <div>
        <label style={labelStyle}>Plan Interest</label>
        <select
          name="plan"
          value={formData.plan}
          onChange={handleChange}
          style={{
            ...inputStyle,
            cursor: 'pointer',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 16px center',
            paddingRight: '40px',
          }}
          onFocus={e => (e.target.style.borderColor = '#3B82F6')}
          onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
        >
          <option value="Free" style={{ background: '#0A0E1A' }}>Free Plan</option>
          <option value="Individual" style={{ background: '#0A0E1A' }}>Individual — ₹899/year</option>
          <option value="Family Shield" style={{ background: '#0A0E1A' }}>Family Shield — ₹1,999/year</option>
        </select>
      </div>

      {error && (
        <p style={{
          fontSize: '13px', color: '#E63946',
          background: 'rgba(230,57,70,0.08)',
          border: '1px solid rgba(230,57,70,0.2)',
          padding: '10px 14px', borderRadius: '8px',
        }}>
          ⚠️ {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary"
        style={{
          width: '100%',
          justifyContent: 'center',
          padding: '16px',
          fontSize: '14px',
          opacity: submitting ? 0.7 : 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? '⏳ Joining...' : '🛡️  Join the Waitlist — Get Early Access'}
      </button>

      <p style={{
        fontSize: '11px',
        color: 'rgba(255,255,255,0.25)',
        textAlign: 'center',
      }}>
        No payment now · Free to join · Notified at launch
      </p>
    </form>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Suraksha() {
  const [activeLayer, setActiveLayer] = useState(0);
  const featuresRef = useRef<HTMLDivElement>(null);

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="page-wrapper" style={{ paddingTop: '80px' }}>
      <SEOHead
        title="Suraksha — India's AI Anti-Fraud Protection App"
        description="Suraksha protects you and your family from cyber fraud, scam calls, AI voice cloning, digital arrest scams, and UPI fraud. India's most advanced anti-fraud mobile app. Coming soon on Android and iOS."
        canonical="/suraksha"
        keywords="anti fraud app India, scam call detection India, digital arrest scam, cyber fraud protection, AI voice clone detection, UPI fraud protection, Suraksha app"
      />

      {/* ── HERO ── */}
      <section
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          padding: '80px 32px',
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%,
              rgba(230,57,70,0.3) 0%,
              rgba(139,92,246,0.15) 40%,
              transparent 70%),
            var(--black)
          `,
        }}
      >
        {/* Decorative orbs */}
        <div style={{
          position: 'absolute', top: '-200px', right: '-200px',
          width: '700px', height: '700px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(230,57,70,0.12) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-100px', left: '-100px',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          width: '100%', position: 'relative',
        }}>
          <div className="suraksha-hero-grid">

            {/* Left — Content */}
            <div>
              <div style={{
                display: 'flex', alignItems: 'center',
                gap: '12px', marginBottom: '32px', flexWrap: 'wrap',
              }}>
                <span className="badge" style={{
                  background: 'rgba(230,57,70,0.1)',
                  borderColor: 'rgba(230,57,70,0.3)',
                  color: '#E63946',
                }}>
                  🛡️ &nbsp; Cybersecurity · AI
                </span>
                <span style={{
                  fontSize: '11px', fontWeight: '700',
                  color: '#F59E0B',
                  background: 'rgba(245,158,11,0.1)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  padding: '6px 14px', borderRadius: '50px',
                  letterSpacing: '1px', textTransform: 'uppercase',
                }}>
                  ● Coming Soon
                </span>
              </div>

              <h1 className="display-heading" style={{ marginBottom: '8px' }}>
                SURA<span style={{
                  background: 'linear-gradient(135deg, #E63946 0%, #FF6B6B 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>KSHA</span>
              </h1>

              <p style={{
                fontSize: 'clamp(16px, 2vw, 22px)',
                color: 'rgba(255,255,255,0.5)',
                letterSpacing: '3px',
                textTransform: 'uppercase',
                fontWeight: '600',
                marginBottom: '32px',
              }}>
                India's AI Anti-Fraud Shield
              </p>

              <p style={{
                fontSize: 'clamp(16px, 2vw, 19px)',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: '1.75',
                maxWidth: '540px',
                marginBottom: '40px',
              }}>
                India loses <strong style={{ color: '#E63946' }}>₹1,750 crore</strong> to
                cyber fraud every year. Scam calls. AI voice clones. Fake CBI officers.
                UPI fraud. Suraksha stops all of it — in real time, before damage is done.
              </p>

              <div style={{
                display: 'flex', gap: '12px',
                flexWrap: 'wrap', marginBottom: '48px',
              }}>
                <button
                  onClick={scrollToFeatures}
                  className="btn-primary"
                >
                  See How It Works ↓
                </button>
                <Link to="/contact" className="btn-outline">
                  Partner With Us →
                </Link>
              </div>

              {/* Trust badges */}
              <div style={{
                display: 'flex', gap: '24px', flexWrap: 'wrap',
              }}>
                {[
                  { icon: '📱', label: 'Android + iOS' },
                  { icon: '🔒', label: 'DPDP Compliant' },
                  { icon: '🇮🇳', label: 'India-First' },
                ].map((item) => (
                  <div key={item.label} style={{
                    display: 'flex', alignItems: 'center',
                    gap: '8px', fontSize: '13px',
                    color: 'rgba(255,255,255,0.4)',
                  }}>
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Phone Mockup */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <div style={{
                width: '280px',
                background: 'rgba(10,14,26,0.9)',
                border: '1px solid rgba(230,57,70,0.25)',
                borderRadius: '40px',
                padding: '20px 16px',
                position: 'relative',
                boxShadow: '0 40px 100px rgba(230,57,70,0.15), 0 0 0 1px rgba(255,255,255,0.05)',
              }}>
                {/* Phone notch */}
                <div style={{
                  width: '80px', height: '6px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '3px',
                  margin: '0 auto 20px',
                }} />

                {/* Alert card */}
                <div style={{
                  background: 'rgba(230,57,70,0.1)',
                  border: '1px solid rgba(230,57,70,0.3)',
                  borderRadius: '16px',
                  padding: '20px 16px',
                  marginBottom: '12px',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: '10px', marginBottom: '12px',
                  }}>
                    <span style={{ fontSize: '24px' }}>🚨</span>
                    <div>
                      <p style={{
                        fontSize: '13px', fontWeight: '700',
                        color: '#E63946', marginBottom: '2px',
                      }}>SCAM DETECTED</p>
                      <p style={{
                        fontSize: '11px',
                        color: 'rgba(255,255,255,0.4)',
                      }}>+91 98XXX XXXXX</p>
                    </div>
                  </div>
                  <p style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.6)',
                    lineHeight: '1.5',
                    marginBottom: '14px',
                  }}>
                    This number matches known CBI impersonation pattern. Do not share OTP or transfer money.
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                  }}>
                    <button style={{
                      background: 'rgba(230,57,70,0.2)',
                      border: '1px solid rgba(230,57,70,0.4)',
                      borderRadius: '8px', padding: '8px',
                      color: '#E63946', fontSize: '12px',
                      fontWeight: '700', cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                    }}>
                      🚫 Block
                    </button>
                    <button style={{
                      background: 'rgba(34,197,94,0.15)',
                      border: '1px solid rgba(34,197,94,0.3)',
                      borderRadius: '8px', padding: '8px',
                      color: '#22C55E', fontSize: '12px',
                      fontWeight: '700', cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                    }}>
                      ✓ Safe
                    </button>
                  </div>
                </div>

                {/* Voice guard card */}
                <div style={{
                  background: 'rgba(139,92,246,0.08)',
                  border: '1px solid rgba(139,92,246,0.2)',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '12px',
                }}>
                  <p style={{
                    fontSize: '11px', fontWeight: '700',
                    color: '#8B5CF6', marginBottom: '8px',
                    letterSpacing: '1px',
                  }}>
                    🤖 VOICEGUARD ACTIVE
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.5)',
                    }}>AI Voice Probability</span>
                    <span style={{
                      fontSize: '16px', fontWeight: '800',
                      fontFamily: 'Barlow Condensed, sans-serif',
                      color: '#F59E0B',
                    }}>87%</span>
                  </div>
                  {/* Progress bar */}
                  <div style={{
                    height: '4px',
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: '2px', marginTop: '8px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', width: '87%',
                      background: 'linear-gradient(90deg, #8B5CF6, #F59E0B)',
                      borderRadius: '2px',
                    }} />
                  </div>
                </div>

                {/* UPI Lock card */}
                <div style={{
                  background: 'rgba(37,99,235,0.08)',
                  border: '1px solid rgba(37,99,235,0.2)',
                  borderRadius: '16px',
                  padding: '16px',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <div>
                      <p style={{
                        fontSize: '11px', fontWeight: '700',
                        color: '#3B82F6', marginBottom: '4px',
                        letterSpacing: '1px',
                      }}>
                        🔒 UPI LOCKED
                      </p>
                      <p style={{
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.4)',
                      }}>Verify family first</p>
                    </div>
                    <p style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '28px', fontWeight: '900',
                      color: '#fff',
                    }}>9:42</p>
                  </div>
                </div>

                {/* Home indicator */}
                <div style={{
                  width: '60px', height: '4px',
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '2px',
                  margin: '20px auto 0',
                }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CRISIS STATS ── */}
      <section style={{
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(230,57,70,0.15)',
        borderBottom: '1px solid rgba(230,57,70,0.15)',
        padding: '0 32px',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}
          className="suraksha-stats-grid">
          {stats.map((stat, i) => (
            <div key={i} style={{
              padding: '40px 24px', textAlign: 'center',
              borderRight: i < 3
                ? '1px solid rgba(230,57,70,0.1)' : 'none',
            }}>
              <p style={{ fontSize: '28px', marginBottom: '8px' }}>{stat.icon}</p>
              <p style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: 'clamp(28px, 4vw, 44px)',
                fontWeight: '900', color: '#fff',
                lineHeight: 1, marginBottom: '8px',
              }}>
                {stat.value}
              </p>
              <p style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.4)',
                lineHeight: '1.4',
              }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 5 LAYER PROTECTION ── */}
      <section
        ref={featuresRef}
        style={{
          padding: '120px 32px',
          background: 'var(--dark)',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">VoiceGuard Technology</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              5 LAYERS OF<br />
              <span style={{
                background: 'linear-gradient(135deg, #E63946 0%, #FF6B6B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>PROTECTION</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              No single layer is enough. Suraksha stacks five independent protection 
              systems — each one catching what the others miss.
            </p>
          </div>

          <div className="layers-grid">
            {/* Left — Layer selector */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
              {protectionLayers.map((layer, i) => (
                <button
                  key={i}
                  onClick={() => setActiveLayer(i)}
                  style={{
                    display: 'flex', alignItems: 'center',
                    gap: '16px', padding: '20px 24px',
                    background: activeLayer === i
                      ? `rgba(${layer.color === '#3B82F6' ? '59,130,246'
                        : layer.color === '#8B5CF6' ? '139,92,246'
                        : layer.color === '#06B6D4' ? '6,182,212'
                        : layer.color === '#F59E0B' ? '245,158,11'
                        : '230,57,70'},0.12)`
                      : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${activeLayer === i
                      ? layer.color + '50'
                      : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.25s',
                    fontFamily: 'Inter, sans-serif',
                    width: '100%',
                  }}
                >
                  <span style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '28px', fontWeight: '900',
                    color: activeLayer === i
                      ? layer.color : 'rgba(255,255,255,0.1)',
                    lineHeight: 1, flexShrink: 0, minWidth: '36px',
                    transition: 'color 0.25s',
                  }}>
                    {layer.number}
                  </span>
                  <div>
                    <p style={{
                      fontSize: '15px', fontWeight: '700',
                      color: activeLayer === i
                        ? '#fff' : 'rgba(255,255,255,0.55)',
                      marginBottom: '2px',
                      transition: 'color 0.25s',
                    }}>
                      {layer.title}
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: activeLayer === i
                        ? layer.color : 'rgba(255,255,255,0.25)',
                      transition: 'color 0.25s',
                    }}>
                      {layer.subtitle}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* Right — Active layer detail */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${protectionLayers[activeLayer].color}30`,
              borderRadius: '24px',
              padding: '56px 48px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '400px',
            }}>
              {/* Background glow */}
              <div style={{
                position: 'absolute', top: '-100px', right: '-100px',
                width: '400px', height: '400px', borderRadius: '50%',
                background: `radial-gradient(circle, ${protectionLayers[activeLayer].color}15 0%, transparent 70%)`,
                pointerEvents: 'none',
                transition: 'background 0.4s',
              }} />

              <div style={{ position: 'relative' }}>
                <div style={{
                  width: '72px', height: '72px',
                  background: `${protectionLayers[activeLayer].color}18`,
                  border: `1px solid ${protectionLayers[activeLayer].color}40`,
                  borderRadius: '20px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '36px', marginBottom: '28px',
                }}>
                  {protectionLayers[activeLayer].icon}
                </div>

                <p style={{
                  fontSize: '11px', fontWeight: '700',
                  color: protectionLayers[activeLayer].color,
                  letterSpacing: '3px', textTransform: 'uppercase',
                  marginBottom: '12px',
                }}>
                  Layer {protectionLayers[activeLayer].number} — {protectionLayers[activeLayer].subtitle}
                </p>

                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 'clamp(28px, 4vw, 44px)',
                  fontWeight: '900', color: '#fff',
                  textTransform: 'uppercase',
                  lineHeight: 1.05, marginBottom: '24px',
                }}>
                  {protectionLayers[activeLayer].title}
                </h3>

                <p style={{
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: '1.8',
                  maxWidth: '500px',
                }}>
                  {protectionLayers[activeLayer].desc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ALL FEATURES ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Full Feature Set</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              EVERYTHING YOU NEED<br />
              <span className="gradient-text">TO STAY SAFE</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              Suraksha is not just a call blocker. It is a complete digital
              safety system built for every member of your family.
            </p>
          </div>

          <div style={{
            display: 'grid', gap: '2px',
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid rgba(59,130,246,0.12)',
            borderRadius: '20px', overflow: 'hidden',
          }} className="suraksha-features-grid">
            {features.map((feature, i) => (
              <div
                key={i}
                style={{
                  padding: '36px',
                  background: 'rgba(5,8,16,0.85)',
                  transition: 'background 0.3s',
                  borderRight: i % 3 !== 2
                    ? '1px solid rgba(59,130,246,0.08)' : 'none',
                  borderBottom: i < 6
                    ? '1px solid rgba(59,130,246,0.08)' : 'none',
                }}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = 'rgba(59,130,246,0.07)')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.background = 'rgba(5,8,16,0.85)')
                }
              >
                <div style={{
                  width: '48px', height: '48px',
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px', marginBottom: '16px',
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '18px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '10px',
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.65',
                }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEVELOPMENT TIMELINE ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="suraksha-timeline-grid">
            <div style={{ position: 'sticky', top: '100px' }}>
              <span className="section-label">Development Progress</span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                WHERE WE<br />
                <span className="gradient-text">ARE NOW</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '32px' }}>
                Suraksha is in active development. The core engine is built. 
                AI Voice Guard is being trained. Public launch is on the horizon. 
                Join the waitlist to be first.
              </p>
              <Link to="#waitlist" className="btn-primary"
                onClick={e => {
                  e.preventDefault();
                  document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
                }}>
                Join the Waitlist →
              </Link>
            </div>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: '0',
            }}>
              {timeline.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '24px',
                  paddingBottom: i < timeline.length - 1 ? '32px' : '0',
                }}>
                  {/* Line + dot */}
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', flexShrink: 0,
                  }}>
                    <div style={{
                      width: '16px', height: '16px',
                      borderRadius: '50%', flexShrink: 0,
                      background: item.status === 'complete'
                        ? '#22C55E'
                        : item.status === 'active'
                        ? '#3B82F6'
                        : 'rgba(255,255,255,0.15)',
                      border: item.status === 'active'
                        ? '3px solid rgba(59,130,246,0.4)'
                        : 'none',
                      boxShadow: item.status === 'active'
                        ? '0 0 16px rgba(59,130,246,0.6)'
                        : 'none',
                      marginTop: '4px',
                    }} />
                    {i < timeline.length - 1 && (
                      <div style={{
                        width: '2px', flex: 1,
                        background: item.status === 'complete'
                          ? 'rgba(34,197,94,0.3)'
                          : 'rgba(255,255,255,0.06)',
                        marginTop: '8px',
                      }} />
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ paddingBottom: '8px' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: '12px', marginBottom: '6px', flexWrap: 'wrap',
                    }}>
                      <span style={{
                        fontSize: '11px', fontWeight: '700',
                        color: item.status === 'complete' ? '#22C55E'
                          : item.status === 'active' ? '#3B82F6'
                          : 'rgba(255,255,255,0.3)',
                        letterSpacing: '2px', textTransform: 'uppercase',
                      }}>
                        {item.phase}
                      </span>
                      <span style={{
                        fontSize: '10px', fontWeight: '700',
                        padding: '3px 10px', borderRadius: '50px',
                        background: item.status === 'complete'
                          ? 'rgba(34,197,94,0.1)'
                          : item.status === 'active'
                          ? 'rgba(59,130,246,0.1)'
                          : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${item.status === 'complete'
                          ? 'rgba(34,197,94,0.3)'
                          : item.status === 'active'
                          ? 'rgba(59,130,246,0.3)'
                          : 'rgba(255,255,255,0.08)'}`,
                        color: item.status === 'complete' ? '#22C55E'
                          : item.status === 'active' ? '#3B82F6'
                          : 'rgba(255,255,255,0.3)',
                        letterSpacing: '1px', textTransform: 'uppercase',
                      }}>
                        {item.status === 'complete' ? '✓ Complete'
                          : item.status === 'active' ? '● In Progress'
                          : 'Upcoming'}
                      </span>
                    </div>
                    <h4 style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '22px', fontWeight: '900',
                      color: item.status === 'upcoming'
                        ? 'rgba(255,255,255,0.35)' : '#fff',
                      textTransform: 'uppercase',
                      marginBottom: '6px',
                    }}>
                      {item.label}
                    </h4>
                    <p style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.35)',
                      lineHeight: '1.6',
                    }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Simple Pricing</span>
            <h2 className="section-title" style={{ marginBottom: '16px' }}>
              PROTECTION FOR<br />
              <span className="gradient-text">EVERY BUDGET</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              Start free. Upgrade when you are ready.
              Cancel anytime. No hidden charges.
            </p>
          </div>

          <div style={{
            display: 'grid', gap: '24px',
            alignItems: 'start',
          }} className="suraksha-pricing-grid">
            {plans.map((plan, i) => (
              <div
                key={i}
                style={{
                  padding: '40px 36px',
                  background: plan.highlight
                    ? 'linear-gradient(135deg, rgba(230,57,70,0.12) 0%, rgba(139,92,246,0.08) 100%)'
                    : 'rgba(255,255,255,0.02)',
                  border: plan.highlight
                    ? '1px solid rgba(230,57,70,0.35)'
                    : '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '20px',
                  position: 'relative',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.5)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: '-14px',
                    left: '50%', transform: 'translateX(-50%)',
                    background: '#E63946',
                    color: '#fff', fontSize: '11px',
                    fontWeight: '700', letterSpacing: '1px',
                    textTransform: 'uppercase',
                    padding: '6px 20px', borderRadius: '50px',
                    whiteSpace: 'nowrap',
                  }}>
                    {plan.badge}
                  </div>
                )}

                <p style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '22px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '6px',
                }}>
                  {plan.name}
                </p>
                <p style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.4)',
                  marginBottom: '24px',
                }}>
                  {plan.desc}
                </p>

                <div style={{
                  display: 'flex', alignItems: 'flex-end',
                  gap: '4px', marginBottom: '8px',
                }}>
                  <span style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '52px', fontWeight: '900',
                    color: '#fff', lineHeight: 1,
                  }}>
                    {plan.price}
                  </span>
                  <span style={{
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.4)',
                    paddingBottom: '8px',
                  }}>
                    {plan.period}
                  </span>
                </div>

                {plan.perDay && (
                  <p style={{
                    fontSize: '12px',
                    color: '#22C55E',
                    marginBottom: '24px',
                    fontWeight: '600',
                  }}>
                    Just {plan.perDay}
                  </p>
                )}

                <div style={{
                  height: '1px',
                  background: 'rgba(59,130,246,0.15)',
                  marginBottom: '24px',
                }} />

                <div style={{
                  display: 'flex', flexDirection: 'column',
                  gap: '10px', marginBottom: '32px',
                }}>
                  {plan.features.map((feature) => (
                    <div key={feature} style={{
                      display: 'flex', alignItems: 'flex-start',
                      gap: '10px', fontSize: '14px',
                      color: 'rgba(255,255,255,0.6)',
                    }}>
                      <span style={{
                        color: '#22C55E', fontSize: '16px',
                        flexShrink: 0, lineHeight: 1.4,
                      }}>✓</span>
                      {feature}
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={plan.highlight ? 'btn-primary' : 'btn-outline'}
                  style={{
                    width: '100%', justifyContent: 'center',
                    padding: '14px',
                  }}
                >
                  {plan.cta} →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WAITLIST ── */}
      <section
        id="waitlist"
        style={{
          padding: '120px 32px',
          background: `
            radial-gradient(ellipse 80% 60% at 50% 0%,
              rgba(230,57,70,0.12) 0%,
              transparent 70%),
            var(--black)
          `,
          borderTop: '1px solid rgba(230,57,70,0.15)',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="waitlist-grid">

            {/* Left — Copy */}
            <div>
              <span className="section-label" style={{ color: '#E63946' }}>
                Early Access
              </span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                BE AMONG<br />
                THE FIRST<br />
                <span style={{
                  background: 'linear-gradient(135deg, #E63946, #FF6B6B)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>PROTECTED</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '40px', maxWidth: '440px' }}>
                Suraksha launches soon on Android and iOS. Join the waitlist now — 
                early access members get priority onboarding, launch pricing, and 
                direct access to the founding team.
              </p>

              {/* What you get */}
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '16px',
              }}>
                {[
                  { icon: '⚡', text: 'Priority access before public launch' },
                  { icon: '💰', text: 'Locked-in launch pricing — never increases' },
                  { icon: '📞', text: 'Direct line to the founding team' },
                  { icon: '🛡️', text: 'Free 3-month Individual plan at launch' },
                ].map((item) => (
                  <div key={item.text} style={{
                    display: 'flex', alignItems: 'center',
                    gap: '14px',
                  }}>
                    <div style={{
                      width: '36px', height: '36px',
                      background: 'rgba(230,57,70,0.1)',
                      border: '1px solid rgba(230,57,70,0.25)',
                      borderRadius: '10px',
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '16px',
                      flexShrink: 0,
                    }}>
                      {item.icon}
                    </div>
                    <p style={{
                      fontSize: '15px',
                      color: 'rgba(255,255,255,0.65)',
                    }}>
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>

              {/* Emergency helpline */}
              <div style={{
                marginTop: '48px',
                padding: '20px 24px',
                background: 'rgba(230,57,70,0.06)',
                border: '1px solid rgba(230,57,70,0.15)',
                borderRadius: '14px',
              }}>
                <p style={{
                  fontSize: '13px', fontWeight: '700',
                  color: '#fff', marginBottom: '4px',
                }}>
                  🚨 Cyber fraud happening right now?
                </p>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
                  Call the National Cybercrime Helpline:{' '}
                  <a href="tel:1930" style={{
                    color: '#E63946', fontWeight: '700',
                    textDecoration: 'none',
                  }}>1930</a>
                  {' '}(Free · 24/7)
                </p>
              </div>
            </div>

            {/* Right — Form */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(59,130,246,0.15)',
              borderRadius: '24px',
              padding: '48px 40px',
            }}>
              <p style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: '22px', fontWeight: '900',
                color: '#fff', textTransform: 'uppercase',
                marginBottom: '8px',
              }}>
                Join the Waitlist
              </p>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.4)',
                marginBottom: '28px',
              }}>
                Launching soon. Be first. Stay safe.
              </p>
              <WaitlistForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{
        padding: '80px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{
            fontSize: '13px', fontWeight: '700',
            color: 'rgba(255,255,255,0.3)',
            letterSpacing: '3px', textTransform: 'uppercase',
            marginBottom: '16px',
          }}>
            Interested in enterprise deployment?
          </p>
          <h3 style={{
            fontFamily: 'Barlow Condensed, sans-serif',
            fontSize: 'clamp(28px, 4vw, 44px)',
            fontWeight: '900', color: '#fff',
            textTransform: 'uppercase', marginBottom: '16px',
          }}>
            PROTECT YOUR ENTIRE<br />
            <span className="gradient-text">ORGANISATION</span>
          </h3>
          <p className="section-subtitle" style={{ margin: '0 auto 32px' }}>
            Banks, NBFCs, hospitals, and enterprises — talk to us about 
            deploying Suraksha at scale for your employees or customers.
          </p>
          <Link to="/contact" className="btn-blue">
            Talk to Our Team →
          </Link>
        </div>
      </section>

      {/* ── RESPONSIVE ── */}
      <style>{`
        .suraksha-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .suraksha-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
        }
        .layers-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          align-items: start;
        }
        .suraksha-features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        .suraksha-timeline-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: start;
        }
        .suraksha-pricing-grid {
          grid-template-columns: repeat(3, 1fr);
        }
        .waitlist-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: start;
        }
        .suraksha-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 1024px) {
          .layers-grid {
            grid-template-columns: 1fr !important;
          }
          .suraksha-features-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .suraksha-pricing-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .suraksha-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .suraksha-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .suraksha-features-grid {
            grid-template-columns: 1fr !important;
          }
          .suraksha-timeline-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .suraksha-pricing-grid {
            grid-template-columns: 1fr !important;
          }
          .waitlist-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .suraksha-form-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}